import {
  Button,
  TextField,
  Box,
  Grid,
  Paper,
  Autocomplete,
  useMediaQuery,
  Alert,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import tenantGroupService from '../tenantGroups/tenantGroupService';
import RegionAutocomplete from '../../components/saas/RegionAutocomplete';
import TimeZoneAutocomplete from '../../components/saas/TimeZoneAutocomplete';
import LanguageSelect from '../../components/saas/LanguageSelect';
import { DEFAULT_IANA_TIME_ZONE } from '../../utils/timeZoneOptions';

export enum TenantTypes {
  Individual = 0,
  Organization = 1,
  Government = 2,
}

export enum TenantStatus {
  Active = 0,
  Suspended = 1,
  PendingDeletion = 2,
  Deleted = 3,
  Archived = 4,
}

const tenantTypesOptions = [
  { value: TenantTypes.Individual, label: 'Individual' },
  { value: TenantTypes.Organization, label: 'Organization' },
  { value: TenantTypes.Government, label: 'Government' },
];

const statusOptions = [
  { value: TenantStatus.Active, label: 'Active' },
  { value: TenantStatus.Suspended, label: 'Suspended' },
  { value: TenantStatus.PendingDeletion, label: 'Pending deletion' },
  { value: TenantStatus.Archived, label: 'Archived' },
];

const slugify = (value: string) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const TenantForm = (props: any) => {
  const {
    entity,
    onSubmit,
    errors,
    handleCancel,
    readonly = false,
    createdApiKey = null,
  } = props;
  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  );
  const isEdit = !!entity?.id;
  const multiTenancyEnabled = !!useSelector(
    (state: any) => state.app.featureFlags?.multiTenancyEnabled
  );
  const showGroupFields = multiTenancyEnabled && !isEdit;
  const [tenantGroups, setTenantGroups] = useState<any[]>([]);

  useEffect(() => {
    if (!showGroupFields) return;
    tenantGroupService.list().then((response) => {
      if (!response.hasError) {
        setTenantGroups(response.records ?? []);
      }
    });
  }, [showGroupFields]);

  return (
    <Formik
      initialValues={entity}
      enableReinitialize={true}
      validate={(values) => {
        const validation: any = {};
        if (!values.name) {
          validation.name = 'Required';
        }
        if (showGroupFields && !values.tenantGroupId) {
          validation.tenantGroupId = 'Required';
        }
        if (showGroupFields && !(values.groupDisplayName || values.name)) {
          validation.groupDisplayName = 'Required';
        }
        if (showGroupFields) {
          const deepLink =
            values.deepLinkSlug ||
            slugify(values.groupDisplayName || values.name || '');
          if (!deepLink) {
            validation.deepLinkSlug = 'Required';
          }
        }
        return validation;
      }}
      onSubmit={(values, { setSubmitting }) => {
        const payload = { ...values };
        if (showGroupFields) {
          payload.groupDisplayName =
            values.groupDisplayName?.trim() || values.name?.trim() || '';
          payload.deepLinkSlug =
            values.deepLinkSlug?.trim() ||
            slugify(payload.groupDisplayName);
        }
        onSubmit(payload, setSubmitting);
      }}
    >
      {({
        isSubmitting,
        values,
        touched,
        handleChange,
        setFieldValue,
        errors: validationErrors,
      }) => (
        <Form>
          <Paper>
            <Grid container spacing={2} sx={{ justifyContent: 'left', p: 5 }}>
              <h2>{isEdit ? 'Bérlő szerkesztése' : 'Új bérlő'}</h2>
              {createdApiKey && (
                <Grid size={12}>
                  <Alert severity="warning">
                    Mobile API key (copy now — shown once): <strong>{createdApiKey}</strong>
                  </Alert>
                </Grid>
              )}
              <Grid size={12} sx={{ pt: 2 }}>
                <Box sx={{ color: 'red' }}>
                  {(errors ?? []).map((error: string) => (
                    <li key={error}>{error}</li>
                  ))}
                </Box>
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.name ?? ''}
                  onChange={(e) => {
                    handleChange(e);
                    if (
                      showGroupFields &&
                      (!values.groupDisplayName ||
                        values.groupDisplayName === values.name)
                    ) {
                      setFieldValue('groupDisplayName', e.target.value);
                      if (
                        !values.deepLinkSlug ||
                        values.deepLinkSlug === slugify(values.groupDisplayName || values.name || '')
                      ) {
                        setFieldValue('deepLinkSlug', slugify(e.target.value));
                      }
                    }
                  }}
                  label="Bérlő név"
                  name="name"
                  required
                  disabled={readonly}
                  fullWidth
                  autoFocus
                  error={!!touched.name && !!validationErrors.name}
                  helperText={
                    !!touched.name &&
                    !!validationErrors.name &&
                    (validationErrors.name as string)
                  }
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.slug ?? ''}
                  onChange={handleChange}
                  label="Slug"
                  name="slug"
                  disabled={readonly}
                  fullWidth
                  helperText="Leave empty on create to auto-generate from name"
                />
              </Grid>
              {showGroupFields && (
                <>
                  <Grid size={12}>
                    <Autocomplete
                      options={tenantGroups}
                      value={
                        tenantGroups.find((g) => g.id === values.tenantGroupId) ?? null
                      }
                      getOptionLabel={(option) => option?.name ?? ''}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onChange={(_, value) =>
                        setFieldValue('tenantGroupId', value?.id ?? null)
                      }
                      disabled={readonly}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Bérlőcsoport"
                          error={
                            !!touched.tenantGroupId && !!validationErrors.tenantGroupId
                          }
                          helperText={
                            (!!touched.tenantGroupId &&
                              (validationErrors.tenantGroupId as string)) ||
                            'Aggregátor városválasztóhoz kötelező'
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      value={values.groupDisplayName ?? ''}
                      onChange={(e) => {
                        setFieldValue('groupDisplayName', e.target.value);
                        if (
                          !values.deepLinkSlug ||
                          values.deepLinkSlug ===
                            slugify(values.groupDisplayName || values.name || '')
                        ) {
                          setFieldValue('deepLinkSlug', slugify(e.target.value));
                        }
                      }}
                      label="Megjelenített név (csoport)"
                      name="groupDisplayName"
                      required
                      disabled={readonly}
                      fullWidth
                      helperText="Picker / shortcut címke — alapértelmezés: bérlő név"
                      error={
                        !!touched.groupDisplayName && !!validationErrors.groupDisplayName
                      }
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      value={values.deepLinkSlug ?? ''}
                      onChange={(e) =>
                        setFieldValue('deepLinkSlug', slugify(e.target.value))
                      }
                      label="Deep link slug"
                      name="deepLinkSlug"
                      required
                      disabled={readonly}
                      fullWidth
                      helperText="Csoporton belüli egyedi URL-safe slug"
                      error={!!touched.deepLinkSlug && !!validationErrors.deepLinkSlug}
                    />
                  </Grid>
                </>
              )}
              <Grid size={12}>
                <Autocomplete
                  value={values.type ?? TenantTypes.Organization}
                  getOptionLabel={(value: any) =>
                    tenantTypesOptions.find((option) => option.value === value)?.label ?? ''
                  }
                  onChange={(_event, newValue: any) => setFieldValue('type', newValue)}
                  disabled={readonly}
                  renderInput={(params) => (
                    <TextField {...params} label="Bérlő típus" variant="outlined" />
                  )}
                  options={tenantTypesOptions.map((option) => option.value)}
                />
              </Grid>
              {isEdit && (
                <Grid size={12}>
                  <Autocomplete
                    value={values.status ?? TenantStatus.Active}
                    getOptionLabel={(value: any) =>
                      statusOptions.find((option) => option.value === value)?.label ?? ''
                    }
                    onChange={(_event, newValue: any) => setFieldValue('status', newValue)}
                    disabled={readonly}
                    renderInput={(params) => (
                      <TextField {...params} label="Status" variant="outlined" />
                    )}
                    options={statusOptions.map((option) => option.value)}
                  />
                </Grid>
              )}
              <Grid size={12}>
                <TextField
                  value={values.contactName ?? ''}
                  onChange={handleChange}
                  label="Contact name"
                  name="contactName"
                  disabled={readonly}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.contactEmail ?? ''}
                  onChange={handleChange}
                  label="Contact email"
                  name="contactEmail"
                  type="email"
                  disabled={readonly}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <RegionAutocomplete
                  value={values.region}
                  disabled={readonly}
                  onChange={(code) => setFieldValue('region', code ?? '')}
                />
              </Grid>
              <Grid size={12}>
                <LanguageSelect
                  value={values.defaultLanguage ?? 'hu'}
                  disabled={readonly}
                  onChange={(lang) => setFieldValue('defaultLanguage', lang)}
                />
              </Grid>
              <Grid size={12}>
                <TimeZoneAutocomplete
                  value={values.timeZone ?? DEFAULT_IANA_TIME_ZONE}
                  disabled={readonly}
                  onChange={(tz) => setFieldValue('timeZone', tz)}
                />
              </Grid>
              <Grid container sx={{ pt: 3, justifyContent: 'left' }}>
                {!readonly && (
                  <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{ p: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth={isSmallScreen}
                      disabled={isSubmitting}
                    >
                      Mentés
                    </Button>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{ p: 1 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth={isSmallScreen}
                    onClick={handleCancel}
                  >
                    Mégse
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Form>
      )}
    </Formik>
  );
};

export default TenantForm;
